import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { useContext, useMemo, useState } from "react";
import { IRulesetListParams } from "types/DefaultRules/DefaultRules";

type ListHook<T> = (arg?: IRulesetListParams | void) => {
  data?: { data: T[] };
  isLoading?: boolean;
};

type CopyHook<T> = () => [
  (arg: { id: string; body: { providerId: string; payerId: number } }) => {
    unwrap: () => Promise<T>;
  },
  { isLoading: boolean }
];

interface IRuleRowLike {
  id: string;
  name?: string;
  groupName?: string;
  applicability?: string;
  providerId?: string | null;
  payerId?: number | null;
  provider?: { name?: string | null } | null;
  payer?: { name?: string | null } | null;
}

type AddFromDefaultButtonProps<T extends IRuleRowLike> = {
  label?: string;
  providerId: string;
  payerId: number;
  entityLabel: string;
  /** Hook that fetches the list; called with no filter so every rule is a candidate (CLE-2264). */
  useList: ListHook<T>;
  /** Mutation hook for copy-to-contract. */
  useCopy: CopyHook<unknown>;
  onSuccess?: () => void;
  /** Controlled open state; when provided the dialog is driven externally. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button (e.g. when opened from another control). */
  hideTrigger?: boolean;
};

/**
 * CLE-2264 — "attach existing rules to this contract", restored on top of the
 * derived model: every rule (Typical/Fallback template or another provider+payer's
 * scoped rule) is a valid source; each selected rule is COPIED into a row scoped
 * to this contract's provider+payer, so it applies immediately. Multi-select
 * replaces the old one-at-a-time template picker.
 */
function AddFromDefaultButton<T extends IRuleRowLike>({
  label = "Add rules",
  providerId,
  payerId,
  entityLabel,
  useList,
  useCopy,
  onSuccess,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: AddFromDefaultButtonProps<T>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (!isControlled) setInternalOpen(value);
    onOpenChange?.(value);
  };
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isCopying, setIsCopying] = useState(false);
  const { current: toast } = useContext(ToastContext);
  const { data, isLoading } = useList();
  const [copy] = useCopy();

  const optionGroups = useMemo(() => {
    const rows = data?.data ?? [];
    const displayName = (r: T) => r.name || r.groupName || r.id.slice(0, 8);
    const templates = rows
      .filter((r) => r.applicability && r.applicability !== "SCOPED")
      .map((r) => ({
        label: `${displayName(r)} (${
          r.applicability === "FALLBACK" ? "Fallback — live" : "Typical"
        })`,
        value: r.id,
      }));
    // Scoped rules from OTHER provider+payer pairs — this pair's own rules
    // already apply, so copying them would only create duplicates.
    const otherPairs = rows
      .filter(
        (r) =>
          r.applicability === "SCOPED" &&
          !(r.providerId === providerId && r.payerId === payerId)
      )
      .map((r) => ({
        label: `${displayName(r)} (${r.provider?.name ?? "—"} — ${
          r.payer?.name ?? "—"
        })`,
        value: r.id,
      }));
    const groups = [];
    if (templates.length > 0) {
      groups.push({ label: "Typical & Fallback rules", items: templates });
    }
    if (otherPairs.length > 0) {
      groups.push({
        label: "Other provider/payer rules",
        items: otherPairs,
      });
    }
    return groups;
  }, [data, providerId, payerId]);

  const onHide = () => {
    setOpen(false);
    setSelectedIds([]);
  };

  const onConfirm = async () => {
    if (selectedIds.length === 0) return;
    setIsCopying(true);
    let copied = 0;
    const failures: string[] = [];
    // Sequential on purpose: per-type overlap checks (e.g. case rates) should see
    // the rules copied just before them.
    for (let i = 0; i < selectedIds.length; i += 1) {
      const id = selectedIds[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await copy({ id, body: { providerId, payerId } }).unwrap();
        copied += 1;
      } catch (e) {
        const err = e as any;
        const body = err?.data;
        let message: string | null = null;
        if (typeof body === "string") message = body;
        else if (body && typeof body === "object") {
          if (typeof body.message === "string") message = body.message;
          else if (typeof body.error === "string") message = body.error;
        }
        failures.push(message || "Unable to copy rule.");
      }
    }
    setIsCopying(false);
    if (copied > 0) {
      toast?.show({
        severity: failures.length > 0 ? "warn" : "success",
        summary: `Copied ${copied} ${entityLabel.toLowerCase()}${
          copied === 1 ? "" : "s"
        }${failures.length ? `, ${failures.length} skipped` : ""}`,
        detail: failures.slice(0, 3).join("; ") || undefined,
      });
      onSuccess?.();
      onHide();
    } else {
      toast?.show({
        severity: "error",
        summary: "Nothing copied",
        detail: failures.slice(0, 3).join("; ") || "Unable to copy rules.",
      });
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          label={label}
          icon="pi pi-copy"
          className="p-button-text p-button-sm"
          onClick={() => setOpen(true)}
        />
      )}
      <Dialog
        header={`${label} — ${entityLabel}`}
        visible={open}
        onHide={onHide}
        style={{ width: "760px", maxWidth: "95vw" }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              label="Cancel"
              className="p-button-text"
              onClick={onHide}
              disabled={isCopying}
            />
            <Button
              label={
                selectedIds.length > 1
                  ? `Add ${selectedIds.length} rules`
                  : "Add"
              }
              icon="pi pi-check"
              onClick={onConfirm}
              disabled={selectedIds.length === 0 || isCopying}
              loading={isCopying}
            />
          </div>
        }
      >
        <p style={{ fontSize: 13, color: "#4b5563", marginTop: 0 }}>
          Select rules to add to this contract. Each selected rule is copied
          into a new rule scoped to this provider and payer, so it applies
          immediately. Sources can be Typical/Fallback templates or another
          provider + payer&apos;s rules.
        </p>
        <MultiSelect
          value={selectedIds}
          options={optionGroups}
          optionGroupLabel="label"
          optionGroupChildren="items"
          optionLabel="label"
          optionValue="value"
          onChange={(e) => setSelectedIds(e.value ?? [])}
          placeholder={isLoading ? "Loading…" : "Select rules"}
          filter
          display="chip"
          style={{ width: "100%" }}
        />
      </Dialog>
    </>
  );
}

export default AddFromDefaultButton;
